import { combineReducers, configureStore } from '@reduxjs/toolkit';
import { baseApi } from '@nablax/sdk/query';
import { sentryReduxEnhancer } from '@nablax/shared/lib';
import {
  authMiddleware,
  authReducer,
  htMiddleware,
  htReducer,
  networkMonitorMiddleware,
  networkMonitorReducer,
  tasksMiddleware,
  tasksReducer,
  uiReducer,
  uploadsReducer,
  userProjectsReducer,
  usersProjectsMiddleware,
} from '@nablax/shared/stores';
import { useDispatch } from 'react-redux';

import { env } from '@/config/env';
import { editorReducers } from '@/features/editor/stores';

import { listenerMiddleware } from './listener-middleware';

const sharedReducer = combineReducers({
  ui: uiReducer,
  auth: authReducer,
  tasks: tasksReducer,
  userProjects: userProjectsReducer,
  uploads: uploadsReducer,
  networkMonitor: networkMonitorReducer,
  ht: htReducer,
});

const sharedMiddlewares = [
  authMiddleware.middleware,
  networkMonitorMiddleware.middleware,
  tasksMiddleware.middleware,
  usersProjectsMiddleware.middleware,
  htMiddleware.middleware,
] as const;

const store = configureStore({
  devTools: env.VITE_REDUX_DEBUG,
  reducer: combineReducers({
    shared: sharedReducer,
    editor: editorReducers,
    [baseApi.reducerPath]: baseApi.reducer,
  }),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware()
      .prepend(...sharedMiddlewares, listenerMiddleware.middleware)
      .concat(baseApi.middleware),
  enhancers: env.VITE_SENTRY_ENABLED
    ? (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer)
    : undefined,
});

export type Store = typeof store;
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = useDispatch.withTypes<AppDispatch>();

export default store;
